import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import '../assets/styles/Relatorios.css';
import GraficoAgro from '../components/Grafico';
import GraficoPizza from '../components/GraficoPizza';
import SearchableDropdown from '../components/SearchableDropdown';
import { GrFormPrevious, GrFormNext } from 'react-icons/gr';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

export default function Relatorios() {
  const anoAtual = new Date().getFullYear();
  const [searchParams] = useSearchParams();
  const familiaSelecionadaUrl = searchParams.get('familia');
  const povoadoSelecionadoUrl = searchParams.get('povoado');
  
  const [dados, setDados] = useState({ 
    regioes: [], 
    povoados: [], 
    familias: [], 
    culturas: [], 
    producoes: [] 
  });
  const [filtroRegiao, setFiltroRegiao] = useState('Todas');
  const [filtroPovoado, setFiltroPovoado] = useState('Todos');
  const [filtroFamilia, setFiltroFamilia] = useState('Todas');
  const [filtroCultura, setFiltroCultura] = useState('Todas');
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Carregar dados da API
  useEffect(() => {
    const carregarDados = async () => {
      try {
        const [regioesRes, povoadosRes, familiasRes, culturasRes, producoesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/regioes/`),
          fetch(`${API_BASE_URL}/povoados/`),
          fetch(`${API_BASE_URL}/familias/`),
          fetch(`${API_BASE_URL}/culturas/`),
          fetch(`${API_BASE_URL}/producao_familia/`)
        ]);

        const [regioes, povoados, familias, culturas, producoes] = await Promise.all([
          regioesRes.json(),
          povoadosRes.json(),
          familiasRes.json(),
          culturasRes.json(),
          producoesRes.json()
        ]);

        setDados({
          regioes: Array.isArray(regioes.productId) ? regioes.productId : [],
          povoados: Array.isArray(povoados.productId) ? povoados.productId : [],
          familias: Array.isArray(familias.productId) ? familias.productId : [],
          culturas: Array.isArray(culturas.productId) ? culturas.productId : [],
          producoes: Array.isArray(producoes.productId) ? producoes.productId : []
        });

        console.log('Dados carregados:', { regioes, povoados, familias, culturas, producoes });
        console.log('Dados completos estruturados:', {
          regioes: Array.isArray(regioes.productId) ? regioes.productId : [],
          povoados: Array.isArray(povoados.productId) ? povoados.productId : [],
          familias: Array.isArray(familias.productId) ? familias.productId : [],
          culturas: Array.isArray(culturas.productId) ? culturas.productId : [],
          producoes: Array.isArray(producoes.productId) ? producoes.productId : []
        });
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      }
    };

    carregarDados();
  }, []);

  // Aplica filtro automático quando vem da URL com ID da família ou povoado
  useEffect(() => {
    // Prioridade: família > povoado
    if (familiaSelecionadaUrl && dados.familias?.length > 0) {
      const familiaSelecionada = dados.familias.find(f => f.id === parseInt(familiaSelecionadaUrl));
      if (familiaSelecionada) {
        setFiltroFamilia(familiaSelecionada.nome_responsavel);
        // Encontra o povoado e região relacionados
        const povoado = dados.povoados?.find(p => p.id === familiaSelecionada.povoado_id);
        if (povoado) {
          setFiltroPovoado(povoado.nome);
          const regiao = dados.regioes?.find(r => r.id === povoado.regiao_id);
          if (regiao) {
            setFiltroRegiao(regiao.nome);
          }
        }
      }
    } else if (povoadoSelecionadoUrl && dados.povoados?.length > 0) {
      const povoadoSelecionado = dados.povoados.find(p => p.id === parseInt(povoadoSelecionadoUrl));
      if (povoadoSelecionado) {
        setFiltroPovoado(povoadoSelecionado.nome);
        // Seleciona também a região correspondente
        const regiao = dados.regioes?.find(r => r.id === povoadoSelecionado.regiao_id);
        if (regiao) {
          setFiltroRegiao(regiao.nome);
        }
        setFiltroFamilia('Todas');
      }
    }
  }, [familiaSelecionadaUrl, povoadoSelecionadoUrl, dados]);

  // Reset da paginação quando filtros mudam
  useEffect(() => {
    setPagina(1);
  }, [filtroRegiao, filtroPovoado, filtroFamilia, filtroCultura, dataInicio, dataFim]);

  // Combina os dados das produções com informações de família, povoado, região e cultura
  const dadosCompletos = useMemo(() => {
    if (!dados.producoes?.length || !dados.familias?.length || !dados.povoados?.length || !dados.regioes?.length || !dados.culturas?.length) {
      return [];
    }

    return dados.producoes.map(producao => {
      const familia = dados.familias.find(f => f.id === producao.familia_id) || {};
      const povoado = dados.povoados.find(p => p.id === familia.povoado_id) || {};
      const regiao = dados.regioes.find(r => r.id === povoado.regiao_id) || {};
      const cultura = dados.culturas.find(c => c.id === producao.cultura_id) || {};

      return {
        id: producao.id,
        familia_id: producao.familia_id,
        familia_nome: familia.nome_responsavel || 'N/A',
        povoado_id: familia.povoado_id,
        povoado_nome: povoado.nome || 'N/A',
        regiao_id: povoado.regiao_id,
        regiao_nome: regiao.nome || 'N/A',
        cultura_id: producao.cultura_id,
        cultura_nome: cultura.nome || 'N/A',
        ano: producao.ano,
        quantidade_produzida: parseFloat(producao.quantidade_produzida) || 0,
        unidade: producao.unidade || '',
        tipo_producao: familia.tipo_producao || 'N/A',
        latitude: familia.latitude,
        longitude: familia.longitude
      };
    });
  }, [dados]);

  // Filtro de dados
  const dadosFiltrados = useMemo(() => {
    const resultado = dadosCompletos.filter(item => {
      const regiaoOk = filtroRegiao === 'Todas' || item.regiao_nome === filtroRegiao;
      const povoadoOk = filtroPovoado === 'Todos' || item.povoado_nome === filtroPovoado;
      const familiaOk = filtroFamilia === 'Todas' || item.familia_nome === filtroFamilia;
      const culturaOk = filtroCultura === 'Todas' || item.cultura_nome === filtroCultura;

      // Comparação de datas - se as datas forem especificadas, filtrar por ano de produção
      let dataOk = true;
      if (dataInicio || dataFim) {
        const anoProducao = item.ano;
        if (dataInicio) {
          const anoInicio = new Date(dataInicio).getFullYear();
          if (anoProducao < anoInicio) dataOk = false;
        }
        if (dataFim) {
          const anoFim = new Date(dataFim).getFullYear();
          if (anoProducao > anoFim) dataOk = false;
        }
      }

      return regiaoOk && povoadoOk && familiaOk && culturaOk && dataOk;
    });
    
    console.log('Dados completos:', dadosCompletos.length);
    console.log('Dados filtrados:', resultado.length);
    if (dadosCompletos.length > 0) {
      console.log('Primeiro item dos dados completos:', dadosCompletos[0]);
    }
    if (resultado.length > 0) {
      console.log('Primeiro item dos dados filtrados:', resultado[0]);
    }
    
    return resultado;
  }, [dadosCompletos, filtroRegiao, filtroPovoado, filtroFamilia, filtroCultura, dataInicio, dataFim]);

  // KPIs baseados nas produções
  const kpiData = useMemo(() => {
    if (dadosFiltrados.length === 0) {
      return {
        totalProducoes: 0,
        producaoTotal: 0,
        mediaQuantidade: 0,
        totalFamilias: 0
      };
    }

    const producaoTotal = dadosFiltrados.reduce((acc, item) => acc + item.quantidade_produzida, 0);
    const totalFamilias = new Set(dadosFiltrados.map(item => item.familia_id)).size;
    const mediaQuantidade = producaoTotal / dadosFiltrados.length;

    return {
      totalProducoes: dadosFiltrados.length,
      producaoTotal: producaoTotal.toFixed(2),
      mediaQuantidade: mediaQuantidade.toFixed(2),
      totalFamilias
    };
  }, [dadosFiltrados]);

  const { totalProducoes, producaoTotal, mediaQuantidade, totalFamilias } = kpiData;

  // Gráfico de produção por ano
  const producaoPorAnoArray = useMemo(() => {
    const anoMap = {};
    
    dadosFiltrados.forEach(item => {
      const ano = item.ano;
      anoMap[ano] = (anoMap[ano] || 0) + item.quantidade_produzida;
    });
    
    return Object.entries(anoMap)
      .map(([ano, quantidade]) => ({ ano: parseInt(ano), quantidade }))
      .sort((a, b) => a.ano - b.ano);
  }, [dadosFiltrados]);

  const graficoLabels = producaoPorAnoArray.map(entry => entry.ano.toString());
  const graficoValues = producaoPorAnoArray.map(entry => entry.quantidade);
  const graficoData = {
    labels: graficoLabels,
    datasets: [{
      label: 'Produção Total',
      data: graficoValues,
      borderColor: 'rgba(75,192,192,1)',
      backgroundColor: 'rgba(75,192,192,0.2)',
      tension: 0.4,
    }]
  };
  const graficoOptions = {
    responsive: true,
    plugins: { 
      legend: { position: 'top' }, 
      title: { 
        display: true, 
        text: 'Produção por Ano'
      } 
    }
  };

  // Gráfico de Culturas (Pizza)
  const culturas = useMemo(() => {
    const counts = {};
    dadosFiltrados.forEach(item => {
      const cultura = item.cultura_nome;
      counts[cultura] = (counts[cultura] || 0) + item.quantidade_produzida;
    });
    return counts;
  }, [dadosFiltrados]);

  const culturasLabels = Object.keys(culturas);
  const culturasData = Object.values(culturas);
  const culturasColors = [
    '#66bb6a', '#42a5f5', '#ffa726', '#ab47bc', '#ef5350', '#8d6e63', '#26a69a', '#d4e157', '#ff7043', '#789262', '#bdbdbd', '#5c6bc0'
  ];
  const pizzaData = {
    labels: culturasLabels,
    datasets: [
      {
        data: culturasData,
        backgroundColor: culturasColors.slice(0, culturasLabels.length),
        borderWidth: 1,
      },
    ],
  };
  const pizzaOptions = {
    responsive: true,
    plugins: {
      legend: { position: 'bottom' },
      title: { display: true, text: 'Produção por Cultura' },
    },
  };

  // Listas para filtros dinâmicos
  const regioes = useMemo(() => {
    if (!dados.regioes) return [];
    return Array.from(new Set(dados.regioes.map(r => r.nome))).sort();
  }, [dados.regioes]);

  const povoadosParaDropdown = useMemo(() => {
    if (!dados.povoados || !dados.regioes) return [];
    if (filtroRegiao === 'Todas') {
      return Array.from(new Set(dados.povoados.map(p => p.nome))).sort();
    }
    const regiaoSelecionada = dados.regioes.find(r => r.nome === filtroRegiao);
    if (!regiaoSelecionada) return [];
    const povoadosDaRegiao = dados.povoados.filter(p => p.regiao_id === regiaoSelecionada.id);
    return Array.from(new Set(povoadosDaRegiao.map(p => p.nome))).sort();
  }, [dados.regioes, dados.povoados, filtroRegiao]);

  const familiasParaDropdown = useMemo(() => {
    const familiasFiltradas = dadosCompletos.filter(item => {
      const regiaoOk = filtroRegiao === 'Todas' || item.regiao_nome === filtroRegiao;
      const povoadoOk = filtroPovoado === 'Todos' || item.povoado_nome === filtroPovoado;
      return regiaoOk && povoadoOk;
    });
    return Array.from(new Set(familiasFiltradas.map(item => item.familia_nome))).sort();
  }, [dadosCompletos, filtroRegiao, filtroPovoado]);

  const culturasParaDropdown = useMemo(() => {
    if (!dados.culturas) return [];
    return Array.from(new Set(dados.culturas.map(c => c.nome))).sort();
  }, [dados.culturas]);


  // Paginação
  const [pagina, setPagina] = useState(1);
  const itensPorPagina = 7;
  const totalPaginas = Math.ceil(dadosFiltrados.length / itensPorPagina);
  const dadosPaginados = dadosFiltrados.slice((pagina - 1) * itensPorPagina, pagina * itensPorPagina);

  // Handlers para filtros em cascata
  const handleRegiaoChange = (novaRegiao) => {
    setFiltroRegiao(novaRegiao);
    setFiltroPovoado('Todos');
    setFiltroFamilia('Todas');
  };

  const handlePovoadoChange = (novoPovoado) => {
    setFiltroPovoado(novoPovoado);
    setFiltroFamilia('Todas');
  };

  const handleFamiliaChange = (novaFamilia) => {
    setFiltroFamilia(novaFamilia);
  };

  const handleCulturaChange = (novaCultura) => {
    setFiltroCultura(novaCultura);
  };

  // Exportar PDF
  function exportarPDF() {
    const doc = new jsPDF();
    const head = [[
      "ID", "Família", "Povoado", "Região", "Cultura", "Ano", "Quantidade", "Unidade", "Tipo Produção"
    ]];
    
    const body = dadosFiltrados.map(item => [
      item.id,
      item.familia_nome,
      item.povoado_nome,
      item.regiao_nome,
      item.cultura_nome,
      item.ano,
      item.quantidade_produzida,
      item.unidade,
      item.tipo_producao
    ]);

    doc.text(`Relatório de Produção Agropecuária - Safra ${anoAtual}`, 14, 15);
    autoTable(doc, {
      head,
      body,
      startY: 20,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [102, 187, 106] }
    });

    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const dataStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    doc.save(`relatorio_producao_familiar_${dataStr}.pdf`);
  }

  // Exportar CSV
  function exportarCSV() {
    const head = [
      "ID", "Família", "Povoado", "Região", "Cultura", "Ano", "Quantidade", "Unidade", "Tipo Produção"
    ];
    const rows = dadosFiltrados.map(item => [
      item.id,
      item.familia_nome,
      item.povoado_nome,
      item.regiao_nome,
      item.cultura_nome,
      item.ano,
      item.quantidade_produzida,
      item.unidade,
      item.tipo_producao
    ]);

    const csvContent = [head, ...rows]
      .map(e => e.map(v => `"${String(v).replace(/"/g, '""')}"`).join(";")).join("\n");
    
    // Adicionar BOM (Byte Order Mark) para UTF-8 para garantir codificação correta
    const BOM = '\uFEFF';
    const csvWithBOM = BOM + csvContent;
    
    const blob = new Blob([csvWithBOM], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    const now = new Date();
    const pad = n => String(n).padStart(2, '0');
    const dataStr = `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}_${pad(now.getHours())}-${pad(now.getMinutes())}`;
    link.setAttribute('download', `relatorio_producao_familiar_${dataStr}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  return (
    <div>
      <header className="relatorios-header">
        <h1>{`Relatório de Produção Familiar - Safra ${anoAtual}`}</h1>
        <div className="filters">
          <div className="filters-row">
            <div className="filter-group">
              <label htmlFor="data-inicio">Ano Início:</label>
              <input 
                id="data-inicio"
                type="number"
                min="2000" 
                max="2030"
                value={dataInicio ? new Date(dataInicio).getFullYear() : ''} 
                onChange={e => setDataInicio(e.target.value ? `${e.target.value}-01-01` : '')}
                placeholder="Ex: 2023"
                title="Filtrar produções a partir deste ano" 
              />
            </div>
            
            <div className="filter-group">
              <label htmlFor="data-fim">Ano Fim:</label>
              <input 
                id="data-fim"
                type="number"
                min="2000" 
                max="2030"
                value={dataFim ? new Date(dataFim).getFullYear() : ''} 
                onChange={e => setDataFim(e.target.value ? `${e.target.value}-12-31` : '')}
                placeholder="Ex: 2024"
                title="Filtrar produções até este ano"
              />
            </div>
            
            <div className="filter-group">
              <label>Região:</label>
              <SearchableDropdown
                options={regioes}
                value={filtroRegiao}
                onChange={handleRegiaoChange}
                placeholder="Regiões"
                allOptionLabel="Todas as Regiões"
              />
            </div>
            
            <div className="filter-group">
              <label>Povoado:</label>
              <SearchableDropdown
                options={povoadosParaDropdown}
                value={filtroPovoado}
                onChange={handlePovoadoChange}
                placeholder="Povoados"
                allOptionLabel="Todos os Povoados"
              />
            </div>
            
            <div className="filter-group">
              <label>Família:</label>
              <SearchableDropdown
                options={familiasParaDropdown}
                value={filtroFamilia}
                onChange={handleFamiliaChange}
                placeholder="Famílias"
                allOptionLabel="Todas as Famílias"
              />
            </div>

            <div className="filter-group">
              <label>Cultura:</label>
              <SearchableDropdown
                options={culturasParaDropdown}
                value={filtroCultura}
                onChange={handleCulturaChange}
                placeholder="Culturas"
                allOptionLabel="Todas as Culturas"
              />
            </div>
          </div>
          
          <div className="filter-actions">
            <button 
              className="btn-clear"
              onClick={() => { 
                setDataInicio(''); 
                setDataFim(''); 
                setFiltroRegiao('Todas'); 
                setFiltroPovoado('Todos'); 
                setFiltroFamilia('Todas');
                setFiltroCultura('Todas');
                setPagina(1);
              }}
              title="Limpar todos os filtros aplicados"
            >
              Limpar Filtros
            </button>
            
            <button 
              className="btn-pdf"
              onClick={exportarPDF}
              title="Exportar relatório em formato PDF"
            >
              Exportar PDF
            </button>
            
            <button 
              className="btn-csv"
              onClick={exportarCSV}
              title="Exportar dados em formato CSV"
            >
              Exportar CSV
            </button>
          </div>
        </div>
      </header>

            {/* Aviso quando não há dados para os filtros selecionados */}
      {dadosFiltrados.length === 0 && (filtroFamilia !== 'Todas' || filtroRegiao !== 'Todas' || filtroPovoado !== 'Todos' || filtroCultura !== 'Todas' || dataInicio || dataFim) && (
        <div style={{
          background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
          border: '1px solid #ff9800',
          borderRadius: '8px',
          padding: '16px 20px',
          margin: '16px 20px',
          textAlign: 'center',
          boxShadow: '0 2px 4px rgba(255, 152, 0, 0.1)'
        }}>
          <strong style={{ color: '#e65100', fontSize: '16px' }}>Nenhum dado encontrado</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#424242' }}>
            Os filtros aplicados não retornaram resultados. Tente:
          </p>
          <ul style={{ 
            margin: '8px 0', 
            padding: '0 0 0 20px', 
            fontSize: '13px', 
            color: '#666666',
            textAlign: 'left',
            display: 'inline-block'
          }}>
            <li>Selecionar um período de anos diferente</li>
            <li>Escolher outra família, região, povoado ou cultura</li>
            <li>Limpar todos os filtros para ver todos os dados</li>
          </ul>
          <button
            onClick={() => { 
              setFiltroRegiao('Todas'); 
              setFiltroPovoado('Todos'); 
              setFiltroFamilia('Todas');
              setFiltroCultura('Todas');
              setDataInicio('');
              setDataFim('');
            }}
            style={{
              background: '#ff9800',
              color: 'white',
              border: 'none',
              padding: '8px 16px',
              borderRadius: '4px',
              fontSize: '13px',
              cursor: 'pointer',
              marginTop: '8px'
            }}
          >
            Limpar Todos os Filtros
          </button>
        </div>
      )}

      <div className="rel-container" id="relatorio-pdf">
        {dadosFiltrados.length > 0 ? (
          <>
            <div className="kpi-cards">
              <div className="kpi-card">
                <h3>Total de Produções</h3>
                <p>{totalProducoes}</p>
              </div>
              <div className="kpi-card">
                <h3>Produção Total</h3>
                <p>{producaoTotal}</p>
              </div>
              <div className="kpi-card">
                <h3>Média por Produção</h3>
                <p>{mediaQuantidade}</p>
              </div>
              <div className="kpi-card">
                <h3>Total de Famílias</h3>
                <p>{totalFamilias}</p>
              </div>
            </div>

            <div className="charts">
              <div className="chart">
                <GraficoAgro data={graficoData} options={graficoOptions} />
              </div>
              <div className="chart">
                <GraficoPizza data={pizzaData} options={pizzaOptions} />
              </div>
            </div>
          </>
        ) : (
          <div style={{
            background: '#f5f5f5',
            border: '1px solid #ddd',
            borderRadius: '8px',
            padding: '40px 20px',
            margin: '20px 0',
            textAlign: 'center',
            color: '#666'
          }}>
            <h3 style={{ color: '#999', marginBottom: '10px' }}>Dados não disponíveis</h3>
            <p>Os filtros aplicados não retornaram dados para exibição dos gráficos.</p>
          </div>
        )}

        <div className="relatorios-table-container">
          {dadosFiltrados.length > 0 ? (
            <>
              <div className="table-scroll-hint">
                <span>← Deslize para ver mais colunas →</span>
              </div>
              <table className="relatorios-table" border="1" cellPadding="8" cellSpacing="0">
                <thead style={{ background: "#66bb6a", color: "#fff" }}>
                  <tr>
                    <th>Família</th>
                    <th>Região</th>
                    <th>Povoado</th>
                    <th>Cultura</th>
                    <th>Quantidade</th>
                    <th>Ano</th>
                  </tr>
                </thead>
                <tbody>
                  {dadosPaginados.map((item, index) => (
                    <tr key={index}>
                      <td>{item.familia_nome}</td>
                      <td>{item.regiao_nome}</td>
                      <td>{item.povoado_nome}</td>
                      <td>{item.cultura_nome}</td>
                      <td>{item.quantidade_produzida} {item.unidade}</td>
                      <td>{item.ano}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          ) : (
            <div style={{
              background: '#f9f9f9',
              border: '1px solid #e0e0e0',
              borderRadius: '8px',
              padding: '60px 20px',
              textAlign: 'center',
              color: '#666'
            }}>
              <h3 style={{ color: '#999', marginBottom: '15px' }}>Nenhuma fazenda encontrada</h3>
              <p style={{ fontSize: '14px', marginBottom: '0' }}>
                {filtroFamilia !== 'Todas' || filtroRegiao !== 'Todas' || filtroPovoado !== 'Todas' || dataInicio || dataFim
                  ? 'Os filtros aplicados não retornaram resultados. Tente ajustar os critérios de busca.'
                  : 'Não há dados de fazendas disponíveis no sistema.'
                }
              </p>
            </div>
          )}
        </div>
        
        {dadosFiltrados.length > 0 && (
          <div className="pagination-container" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '16px 0', gap: 8 }}>
            <button onClick={() => setPagina(p => Math.max(1, p - 1))} disabled={pagina === 1} className="pagination-btn" style={{ background: 'none', border: 'none', cursor: pagina === 1 ? 'not-allowed' : 'pointer', fontSize: 24 }}>
              <GrFormPrevious />
            </button>
            <span className="pagination-info" style={{ margin: '0 12px' }}>Página {pagina} de {totalPaginas}</span>
            <button onClick={() => setPagina(p => Math.min(totalPaginas, p + 1))} disabled={pagina === totalPaginas} className="pagination-btn" style={{ background: 'none', border: 'none', cursor: pagina === totalPaginas ? 'not-allowed' : 'pointer', fontSize: 24 }}>
              <GrFormNext />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}